import { useMemo, useState } from 'react';
import { AGE_BANDS } from '@guabi/shared';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Pill } from '@/components/ui/Pill';
import { TextInput } from '@/components/ui/Field';
import { useApplyImport, type ImportProposal, type ImportReport } from './queries';

/**
 * The step between reading a file and saving it.
 *
 * It exists because neither source can be trusted to be right: a contacts export
 * is someone's whole address book and almost none of it is wedding guests, and an
 * extraction from a photograph is a good first draft rather than an answer. The
 * proposal is shown, edited and deselected here; only the button at the bottom
 * writes anything.
 */
export function ImportReview({
  wedId,
  proposal,
  open,
  onClose,
  onDone,
}: {
  wedId: string;
  proposal: ImportProposal;
  open: boolean;
  onClose: () => void;
  onDone: (report: ImportReport) => void;
}) {
  const { t } = useTranslation();
  const apply = useApplyImport(wedId);

  // Edits live here rather than mutating the proposal, so closing without
  // importing leaves nothing behind.
  const [households, setHouseholds] = useState(proposal.households);
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(proposal.defaultSelection === 'all' ? proposal.households.map((_, i) => i) : []),
  );
  const [error, setError] = useState<string>();

  const people = useMemo(
    () => households.reduce((n, h, i) => (selected.has(i) ? n + h.members.length : n), 0),
    [households, selected],
  );

  const toggle = (i: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const allOn = selected.size === households.length && households.length > 0;

  /**
   * Edits one field of one proposed person.
   *
   * It takes the age band as well as the names because a file that said "child"
   * in a word the parser did not know came in as an adult, **and the review did
   * not show it** — so the only way to find out was to look at the list
   * afterwards and wonder. A field this screen cannot show is a field it cannot
   * be trusted to have read.
   */
  const edit = (
    hi: number,
    mi: number,
    field: 'firstName' | 'lastName' | 'ageBand',
    value: string,
  ) =>
    setHouseholds((prev) =>
      prev.map((h, i) =>
        i === hi
          ? {
              ...h,
              members: h.members.map((m, j) => (j === mi ? { ...m, [field]: value } : m)),
            }
          : h,
      ),
    );

  async function onImport() {
    setError(undefined);
    const chosen = households
      .filter((_, i) => selected.has(i))
      // A name emptied in the review would fail the schema; drop the row instead.
      .map((h) => ({ ...h, members: h.members.filter((m) => m.firstName.trim().length > 0) }))
      .filter((h) => h.members.length > 0);

    if (chosen.length === 0) {
      setError(t('import.nothingSelected'));
      return;
    }

    try {
      onDone(await apply.mutateAsync({ households: chosen }));
      onClose();
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  return (
    <Drawer open={open} title={t('import.reviewTitle')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-body text-leaf-soft">
          {proposal.source === 'photo' ? t('import.checkPhoto') : t('import.checkFile')}
        </p>

        {proposal.errors.length > 0 && (
          <details className="rounded border border-line p-3">
            <summary className="cursor-pointer text-caption text-status-declinedFg">
              {t('import.notes', { count: proposal.errors.length })}
            </summary>
            <ul className="mt-2 flex flex-col gap-1">
              {proposal.errors.slice(0, 50).map((e) => (
                <li key={`${String(e.line)}-${e.message}`} className="text-caption text-leaf-soft">
                  {e.line > 0 ? `${t('guests.line')} ${String(e.line)}: ` : ''}
                  {e.message}
                </li>
              ))}
            </ul>
          </details>
        )}

        {proposal.ignoredColumns.length > 0 && (
          <p className="text-caption text-leaf-soft">
            {t('guests.ignoredColumns')}: {proposal.ignoredColumns.join(', ')}
          </p>
        )}

        <label className="flex items-center gap-2 text-caption font-medium text-ink">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line"
            checked={allOn}
            onChange={() => setSelected(allOn ? new Set() : new Set(households.map((_, i) => i)))}
          />
          {t('import.selectAll')}
        </label>

        <ul className="flex flex-col gap-3">
          {households.map((h, hi) => (
            <li
              key={`${h.label}-${String(hi)}`}
              className="flex flex-col gap-2 border-b border-line pb-3"
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-line"
                  checked={selected.has(hi)}
                  onChange={() => toggle(hi)}
                />
                <span className="text-body">{h.label}</span>
                {h.members.length > 1 && (
                  <Pill>{t('guests.peopleCount', { count: h.members.length })}</Pill>
                )}
              </label>

              {selected.has(hi) &&
                h.members.map((m, mi) => (
                  <div key={`${String(hi)}-${String(mi)}`} className="flex flex-wrap gap-2 pl-6">
                    <TextInput
                      className="min-w-[7rem] flex-1"
                      aria-label={t('guests.firstName')}
                      value={m.firstName}
                      onChange={(e) => edit(hi, mi, 'firstName', e.target.value)}
                    />
                    <TextInput
                      className="min-w-[7rem] flex-1"
                      aria-label={t('guests.lastName')}
                      value={m.lastName ?? ''}
                      onChange={(e) => edit(hi, mi, 'lastName', e.target.value)}
                    />
                    {/* Shown on every row, not only where it was read, because
                        "adult" here is as often a default the parser fell back to
                        as something the file actually said — and a child counted
                        as an adult is a seat, a meal and a headcount wrong. */}
                    <label className="self-center">
                      <span className="sr-only">{t('guests.ageBand')}</span>
                      <select
                        value={m.ageBand ?? 'adult'}
                        onChange={(e) => edit(hi, mi, 'ageBand', e.target.value)}
                        className="min-h-tap rounded border border-line bg-paper px-2 text-caption"
                      >
                        {AGE_BANDS.map((band) => (
                          <option key={band} value={band}>
                            {t(`guests.ageBandValue.${band}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {(m.email ?? m.phone) && (
                      <span className="self-center text-caption text-leaf-soft">
                        {m.email ?? m.phone}
                      </span>
                    )}
                  </div>
                ))}
            </li>
          ))}
        </ul>

        {error && (
          <p role="alert" className="text-body text-status-declinedFg">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => void onImport()} loading={apply.isPending}>
            {t('import.importCount', { count: people })}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
